import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import DropdownMenu from 'react-dd-menu';
import { connect } from 'react-redux';
import { MenuItem } from 'components/menu/MenuItems';
import NavItem from 'components/app/topbar/navigation/NavItem';
import {
    hasNativeAnalyticsFeature,
    hasSegmentationDashboard,
    hasBalanceSheetOverviewEnabled,
    selectCurrentUser,
} from 'state/user/selectors';
import { selectUsersById } from 'state/users/selectors';
import { withRouter } from 'react-router-dom';
import { RouteUrls } from 'routes/RouteConstants';
import { AppFeaturePermissions, allAccessPermissions } from 'contexts';

class AnalyticsMenu extends PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            isMenuOpen: false,
        };

        this.toggleMenu = this.toggleMenu.bind(this);
    }

    toggleMenu() {
        this.setState({ isMenuOpen: !this.state.isMenuOpen });
        if (this.state.isMenuOpen && !this.props.isAnalyticsPage) {
            this.handleAnalyticsClick();
        }
    }

    handleAnalyticsClick() {
        const { hasNativeAnalytics, history } = this.props;

        if (hasNativeAnalytics) {
            history.push(RouteUrls.ANALYTICS_FINANCIAL_OVERVIEW);
        }
    }

    // NOTE: The button is what you would expect render() to return - has "Analytics" text etc
    renderButton() {
        const { isAnalyticsPage, isLarge, isRanking, isSegmentation } =
            this.props;

        return (
            <NavItem
                title={isLarge ? 'Analytics' : ''}
                active={isAnalyticsPage || isRanking || isSegmentation}
                icon="./assets/img/Analytics-Icon-White@2x.png"
                onClick={this.toggleMenu}
                hideTooltip={true}
                dropdown
            >
                <i
                    className="fa-solid fa-caret-down"
                    size="lg"
                    style={{
                        paddingLeft: isLarge ? '6px' : '3px',
                        fontSize: '16px',
                    }}
                />
            </NavItem>
        );
    }

    render() {
        const {
            isOverview,
            isRanking,
            isSegmentation,
            hasNativeAnalytics,
            hasNativeAnalyticsRanking,
            isPortfolioSegmentationEnabled,
            isBalanceSheetOverview,
            isBalanceSheetOverviewEnabled,
            permissions,
        } = this.props;

        const showBalanceSheetOverview = permissions?.includes(
            AppFeaturePermissions.BalanceSheetOverview
        );
        const showRanking = permissions?.includes(
            AppFeaturePermissions.Ranking
        );
        const showFinancialOverview = permissions?.includes(
            AppFeaturePermissions.FinancialOverview
        );
        const showPortfolioSummary = permissions?.includes(
            AppFeaturePermissions.PortfolioSummary
        );

        if (
            !showBalanceSheetOverview &&
            !showRanking &&
            !showFinancialOverview &&
            !showPortfolioSummary
        ) {
            return null;
        }

        // TODO (Daniel): refactor menu defiinition when more modules are added to Analytics 3.0
        if (hasNativeAnalytics) {
            return (
                <DropdownMenu
                    animate={false}
                    align="left"
                    isOpen={this.state.isMenuOpen}
                    toggle={this.renderButton()}
                    close={this.toggleMenu}
                >
                    {showPortfolioSummary && isPortfolioSegmentationEnabled && (
                        <MenuItem
                            active={isSegmentation}
                            style={{ width: '180px' }}
                            to={RouteUrls.SEGMENTATION}
                        >
                            <span>Portfolio Summary</span>
                        </MenuItem>
                    )}
                    {showFinancialOverview && (
                        <MenuItem
                            active={isOverview}
                            style={{ width: '180px' }}
                            to={RouteUrls.ANALYTICS_FINANCIAL_OVERVIEW}
                        >
                            <span>Financial Overview</span>
                        </MenuItem>
                    )}
                    {isBalanceSheetOverviewEnabled &&
                        showBalanceSheetOverview && (
                            <MenuItem
                                active={isBalanceSheetOverview}
                                style={{ width: '180px' }}
                                to={RouteUrls.ANALYTICS_BALANCE_SHEET_OVERVIEW}
                            >
                                <span>Balance Sheet Overview</span>
                            </MenuItem>
                        )}
                    {hasNativeAnalyticsRanking && showRanking && (
                        <MenuItem
                            active={isRanking}
                            style={{ width: '180px' }}
                            to={RouteUrls.ANALYTICS_RANKING}
                        >
                            <span>Ranking</span>
                        </MenuItem>
                    )}
                </DropdownMenu>
            );
        }

        return null;
    }
}

AnalyticsMenu.propTypes = {
    pureid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    portfolioID: PropTypes.number,
    isLarge: PropTypes.bool,
    isOverview: PropTypes.bool,
    entityType: PropTypes.string,
    isAnalyticsPage: PropTypes.bool,
    hasNativeAnalytics: PropTypes.bool,
    permissions: PropTypes.array,
};

AnalyticsMenu.contextTypes = {
    router: PropTypes.object,
};

export default withRouter(
    connect((state) => {
        const {
            browser: { mediaType },
        } = state;

        const {
            routing: { locationBeforeTransitions },
        } = state;

        const pathname = locationBeforeTransitions.pathname;

        const {
            user: { configuration: config },
        } = state;

        const { FEATURE_NATIVE_ANALYTICS_RANKING: hasNativeAnalyticsRanking } =
            config;

        const isAnalyticsPage = pathname.includes('/analytics');
        const isRanking = pathname.includes(RouteUrls.ANALYTICS_RANKING);
        const isOverview = pathname.includes(
            RouteUrls.ANALYTICS_FINANCIAL_OVERVIEW
        );
        const isBalanceSheetOverview = pathname.includes(
            RouteUrls.ANALYTICS_BALANCE_SHEET_OVERVIEW
        );
        const isSegmentation = pathname.includes(RouteUrls.SEGMENTATION);
        const currentUser = selectCurrentUser(state);
        const currentUserFeaturePermissions = selectUsersById(state, {
            userIds: [currentUser?.id],
        })[0]?.featurePermissions;

        return {
            isLarge: mediaType === 'lg',
            isOverview,
            isBalanceSheetOverview,
            isRanking,
            isAnalyticsPage,
            isSegmentation,
            hasNativeAnalytics: hasNativeAnalyticsFeature(state),
            isPortfolioSegmentationEnabled: hasSegmentationDashboard(state),
            isBalanceSheetOverviewEnabled:
                hasBalanceSheetOverviewEnabled(state),
            hasNativeAnalyticsRanking,
            permissions: currentUserFeaturePermissions?.length
                ? currentUserFeaturePermissions
                : allAccessPermissions,
        };
    })(AnalyticsMenu)
);
